import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import type { GrammarLevel } from '@org/grammar-content';

// Visual ladder for the CEFR levels of the Grammar Aid: the tint deepens as
// the level rises, using only the feminist poster palette shared by the
// student pages (cream / blush / charm / max red / dark).
const LEVEL_COLORS: Record<
  GrammarLevel,
  { background: string; color: string; border: string }
> = {
  A1: { background: '#FDF0ED', color: '#D41920', border: '#F0DAD5' },
  A2: { background: '#F8D7DD', color: '#D41920', border: '#F2C3CD' },
  B1: { background: '#F2A0C4', color: '#2D1A1F', border: '#F2A0C4' },
  B2: { background: '#D41920', color: '#FFFFFF', border: '#D41920' },
  C1: { background: '#2D1A1F', color: '#FFFFFF', border: '#2D1A1F' },
};

export function levelColors(level: GrammarLevel) {
  return LEVEL_COLORS[level];
}

const oswald = "'Oswald', sans-serif";

type Props = {
  level: GrammarLevel;
  /** Also print the level name ("Básico") after the code. */
  withName?: boolean;
  size?: 'sm' | 'md';
  style?: CSSProperties;
};

export function GrammarLevelPill({
  level,
  withName = false,
  size = 'sm',
  style,
}: Props) {
  const { t } = useTranslation('p2e');
  const colors = LEVEL_COLORS[level];
  const name = t(`grammarAid.levels.${level}`);
  return (
    <span
      title={t('grammarAid.levelBadge', { level: `${level} · ${name}` })}
      aria-label={t('grammarAid.levelBadge', { level: `${level} ${name}` })}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        fontFamily: oswald,
        fontWeight: 700,
        fontSize: size === 'sm' ? 11 : 12,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        lineHeight: 1,
        padding: size === 'sm' ? '4px 7px' : '6px 10px',
        borderRadius: 4,
        background: colors.background,
        color: colors.color,
        border: `1px solid ${colors.border}`,
        whiteSpace: 'nowrap',
        flexShrink: 0,
        ...style,
      }}
    >
      {level}
      {withName && (
        <span
          style={{ fontWeight: 500, letterSpacing: '0.06em', opacity: 0.92 }}
        >
          {name}
        </span>
      )}
    </span>
  );
}
